import _ from 'lodash'
import { extractLayoutToNonCoveredRanges, LayoutWithBreakpointRange } from './layoutToNonCoveredRanges'
import { SelectorToCssMap } from './layoutToCss'
import { LayoutDataItems, ItemLayouts } from '@wix/thunderbolt-becky-types'

interface CssWithMedia {
	[mediaQuery: string]: SelectorToCssMap
}

interface BreakpointsToCssWithMedia {
	[breakpointId: string]: CssWithMedia
}

export const mergeCssWithMediaArray = (array: Array<BreakpointsToCssWithMedia>) =>
	array.reduce(_.merge, {}) as BreakpointsToCssWithMedia

export const extractDirectSiblingsOfStickyPerBreakpoint = (
	setsOfSiblings: Array<
		Array<{
			compId: string
			stickyBreakpointList: Array<{ breakpointId: string; mediaQuery: string }>
		}>
	>
): { [breakpointId: string]: { [mediaQuery: string]: Array<string> } } => {
	const breakpointToMediaQueryToComponents: {
		[breakpointId: string]: { [mediaQuery: string]: Array<string> }
	} = {}

	setsOfSiblings.forEach((siblings) => {
		siblings.forEach((sibling, currentSiblingIndex) => {
			const isStickyAndHasDirectSibling =
				sibling.stickyBreakpointList.length > 0 && currentSiblingIndex + 1 < siblings.length

			if (isStickyAndHasDirectSibling) {
				// For all breakpoints in which this component is sticky
				sibling.stickyBreakpointList.forEach(({ mediaQuery, breakpointId }) => {
					const siblingsAfterSticky = siblings.slice(currentSiblingIndex + 1)
					// Traverse all next siblings, and for every non sticky one - add it to breakpointToMediaQueryToComponents
					siblingsAfterSticky.forEach((siblingOfSticky) => {
						const isSiblingNonSticky =
							siblingOfSticky.stickyBreakpointList.length === 0 ||
							siblingOfSticky.stickyBreakpointList.find(
								(isStickyInBreakpoint) => isStickyInBreakpoint.breakpointId !== breakpointId
							)

						if (isSiblingNonSticky) {
							const compArray = breakpointToMediaQueryToComponents[breakpointId]?.[mediaQuery] || []

							_.set(
								breakpointToMediaQueryToComponents,
								[breakpointId, mediaQuery],
								[...compArray, siblingOfSticky.compId]
							)
						}
					})
				})
			}
		})
	})

	return breakpointToMediaQueryToComponents
}

const extractHiddenToNonCoveredRanges = extractLayoutToNonCoveredRanges('ComponentLayout', 'hidden', (v) => !!v)
const extractAspectRatioToNonCoveredRanges = extractLayoutToNonCoveredRanges(
	'ComponentLayout',
	'height',
	(v) => !!v && v.type === 'aspectRatio'
)
const extractStickyToNonCoveredRanges = (type: ItemLayouts['type']) =>
	extractLayoutToNonCoveredRanges(type, 'position', (v) => !!v && v === 'sticky', ['top', 'bottom', 'left', 'right'])

export const extractPropertiesToNonCoveredRanges = <T extends LayoutDataItems>(
	layouts: Array<LayoutWithBreakpointRange<T>>,
	bpOrder: Array<string>
) => {
	const itemLayoutTypes: Array<ItemLayouts['type']> = [
		'GridItemLayout',
		'FixedItemLayout',
		'FlexItemLayout',
		'StackItemLayout',
		'AbsoluteItemLayout',
		'OrganizerItemLayout',
	]
	const layoutsAfterHiddenExtraction = extractHiddenToNonCoveredRanges(layouts, bpOrder)
	let layoutsAfterStickyExtraction = layoutsAfterHiddenExtraction
	_.forEach(itemLayoutTypes, (type) => {
		layoutsAfterStickyExtraction = extractStickyToNonCoveredRanges(type)(layoutsAfterStickyExtraction, bpOrder)
	})
	return extractAspectRatioToNonCoveredRanges(layoutsAfterStickyExtraction, bpOrder)
}

export { layoutDataItemToCss } from './layoutToCss'
